import type {
  ProviderAuthenticationIntentResponse,
  OauthUrlResponse,
  ProviderAuthenticationCheckpointDetails,
  PublicAccount,
} from '@unipile/types';
import { isHttpErrorDetailsPayload } from '@unipile/types';
import { isAxiosError } from 'axios';
import { useState, type PropsWithChildren } from 'react';
import { useProvider } from '../../hooks/useProvider';
import { CheckpointScreen } from './checkpoints/CheckpointScreen';
import { LoginWizardContainer } from './containers/LoginWizardContainer';

export function ClassicWizard({
  onSuccess,
  onError,
  onCancel,
  Component,
}: PropsWithChildren<{
  onSuccess: (response: PublicAccount | OauthUrlResponse) => void;
  onError: (error: unknown) => void;
  onCancel: () => void;
  Component: ({
    onSuccess,
    onError,
    onCancel,
  }: {
    onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
    onError: (error: unknown) => void;
    onCancel: () => void;
  }) => React.JSX.Element;
}>) {
  const [checkpoint, setCheckpoint] = useState<ProviderAuthenticationCheckpointDetails>();
  const { provider } = useProvider();

  const handleSuccess = (response: ProviderAuthenticationIntentResponse) => {
    if (response.object === 'AuthenticationCheckpoint') setCheckpoint(response);
    if (response.object === 'Account') onSuccess(response);
  };

  if (checkpoint)
    return (
      <CheckpointScreen
        checkpoint={checkpoint}
        onSuccess={handleSuccess}
        onError={(error) => {
          if (isAxiosError(error) && isHttpErrorDetailsPayload(error.response?.data)) {
            const errorType = error.response.data.type;

            // On auth flow breaking errors, get back on credentials screen
            if (
              errorType === 'provider/invalid_credentials' ||
              errorType === 'provider/insufficient_permissions' ||
              errorType === 'provider/password_reset_required' ||
              errorType === 'provider/unknown_authentication_context'
            ) {
              setCheckpoint(undefined);
            }
          }

          // Instagram's in-app validation is not retried after a failure: the screen would keep waiting with no way out
          if (provider === 'instagram' && checkpoint.checkpoint.type === 'IN_APP_VALIDATION') setCheckpoint(undefined);

          onError(error);
        }}
      />
    );

  return (
    <LoginWizardContainer>
      <Component onSuccess={handleSuccess} onError={onError} onCancel={onCancel} />
    </LoginWizardContainer>
  );
}
